import { Box } from '@mui/material';
import { keyframes } from '@mui/system';

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const drawLine = keyframes`
  from {
    stroke-dashoffset: 200;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export function CareCircle() {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Box
        component="svg"
        viewBox="10 15 600 480"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        sx={{
          width: '100%',
          maxWidth: 600,
          height: 'auto',
          '& .profile-image': {
            opacity: 0,
            animation: `${fadeInScale} 0.6s ease-out forwards`,
          },
          '& .profile-text': {
            opacity: 0,
            animation: `${fadeInUp} 0.5s ease-out forwards`,
          },
          '& .connector': {
            strokeDasharray: 200,
            strokeDashoffset: 200,
            animation: `${drawLine} 0.6s ease-out forwards`,
          },
          // Staggered delays for each profile
          '& #client-profile .profile-image': { animationDelay: '0s' },
          '& #client-profile .profile-text': { animationDelay: '0.3s' },

          '& #connectors-1 .connector': { animationDelay: '0.5s' },
          '& #profile-1 .profile-image': { animationDelay: '0.5s' },
          '& #profile-1 .profile-text': { animationDelay: '0.7s' },

          '& #connectors-2 .connector': { animationDelay: '1s' },
          '& #profile-2 .profile-image': { animationDelay: '1s' },
          '& #profile-2 .profile-text': { animationDelay: '1.2s' },

          '& #connectors-3 .connector': { animationDelay: '1.5s' },
          '& #profile-3 .profile-image': { animationDelay: '1.5s' },
          '& #profile-3 .profile-text': { animationDelay: '1.7s' },

          '& #connectors-4 .connector': { animationDelay: '2s' },
          '& #profile-4 .profile-image': { animationDelay: '2s' },
          '& #profile-4 .profile-text': { animationDelay: '2.2s' },

          '& #connectors-5 .connector': { animationDelay: '2.5s' },
          '& #profile-5 .profile-image': { animationDelay: '2.5s' },
          '& #profile-5 .profile-text': { animationDelay: '2.7s' },

          '& #connectors-6 .connector': { animationDelay: '3s' },
          '& #profile-6 .profile-image': { animationDelay: '3s' },
          '& #profile-6 .profile-text': { animationDelay: '3.2s' },
        }}
      >
        <defs>
          <linearGradient id="connectorGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#93C5FD" stopOpacity="0.1" />
            <stop offset="25%" stopColor="#75B2FB" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#60A5FA" stopOpacity="0.8" />
            <stop offset="75%" stopColor="#79B5FB" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#93C5FD" stopOpacity="0.1" />
          </linearGradient>
          <linearGradient id="lineToClient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="22%" stopColor="#93C5FD" stopOpacity="0" />
            <stop offset="66%" stopColor="#93C5FD" stopOpacity="1" />
            <stop offset="100%" stopColor="#93C5FD" stopOpacity="1" />
          </linearGradient>

          {/* Circular clip paths for profile images */}
          <clipPath id="clipClient">
            <circle cx="302" cy="246" r="54" />
          </clipPath>
          <clipPath id="clipProfile1">
            <circle cx="397" cy="73" r="38" />
          </clipPath>
          <clipPath id="clipProfile2">
            <circle cx="491" cy="245" r="38" />
          </clipPath>
          <clipPath id="clipProfile3">
            <circle cx="397" cy="421" r="38" />
          </clipPath>
          <clipPath id="clipProfile4">
            <circle cx="200" cy="421" r="38" />
          </clipPath>
          <clipPath id="clipProfile5">
            <circle cx="112" cy="245" r="38" />
          </clipPath>
          <clipPath id="clipProfile6">
            <circle cx="200" cy="74" r="38" />
          </clipPath>
        </defs>

        {/* All connectors first (below profiles) */}
        <g id="connectors-1">
          <path className="connector" d="M397 73 L302 246" stroke="url(#lineToClient)" strokeWidth="2.4" />
          <path className="connector" d="M397 73 C426 90 450 114 466 143 C483 172 492 205 492 238" stroke="url(#connectorGradient)" strokeWidth="4.8" fill="none" />
        </g>
        <g id="connectors-2">
          <path className="connector" d="M491 245 L302 246" stroke="url(#lineToClient)" strokeWidth="2.4" />
          <path className="connector" d="M491 245 C491 278 482 311 466 340 C449 368 426 392 397 409" stroke="url(#connectorGradient)" strokeWidth="4.8" fill="none" />
        </g>
        <g id="connectors-3">
          <path className="connector" d="M397 421 L302 246" stroke="url(#lineToClient)" strokeWidth="2.4" />
          <path className="connector" d="M397 421 C368 438 335 447 302 447 C269 447 236 438 207 421" stroke="url(#connectorGradient)" strokeWidth="4.8" fill="none" />
        </g>
        <g id="connectors-4">
          <path className="connector" d="M200 421 L302 246" stroke="url(#lineToClient)" strokeWidth="2.4" />
          <path className="connector" d="M200 421 C171 404 147 380 131 351 C114 322 105 289 105 256" stroke="url(#connectorGradient)" strokeWidth="4.8" fill="none" />
        </g>
        <g id="connectors-5">
          <path className="connector" d="M112 245 L302 246" stroke="url(#lineToClient)" strokeWidth="2.4" />
          <path className="connector" d="M112 245 C112 212 121 179 137 150 C154 121 178 98 207 81" stroke="url(#connectorGradient)" strokeWidth="4.8" fill="none" />
        </g>
        <g id="connectors-6">
          <path className="connector" d="M200 74 L302 246" stroke="url(#lineToClient)" strokeWidth="2.4" />
          <path className="connector" d="M200 74 C229 57 262 48 295 48 C328 48 361 57 390 74" stroke="url(#connectorGradient)" strokeWidth="4.8" fill="none" />
        </g>

        {/* Client Profile - Center (on top of connectors) */}
        <g id="client-profile">
          <g className="profile-image">
            <image
              x="248"
              y="192"
              width="108"
              height="108"
              href="/images/professionals/care-circle-client.png"
              clipPath="url(#clipClient)"
              preserveAspectRatio="xMidYMid slice"
            />
            <circle cx="302" cy="246" r="54" fill="none" stroke="#99ABAB" strokeWidth="3" />
          </g>
          <g className="profile-text">
            <text x="302" y="322" textAnchor="middle" fontSize="19" fontWeight="600" fill="#666666">Sharon</text>
            <text x="302" y="342" textAnchor="middle" fontSize="16" fontWeight="600" fill="#4F46E5">Stroke Survivor</text>
          </g>
        </g>

        {/* Profile 1 - Husband (top right) */}
        <g id="profile-1">
          <g className="profile-image">
            <image
              x="359"
              y="35"
              width="76"
              height="76"
              href="/images/professionals/care-circle-husband.png"
              clipPath="url(#clipProfile1)"
              preserveAspectRatio="xMidYMid slice"
            />
            <circle cx="397" cy="73" r="38" fill="none" stroke="#99ABAB" strokeWidth="2.4" />
          </g>
          <g className="profile-text">
            <text x="447" y="65" fontSize="17" fontWeight="600" fill="#666666">Fred</text>
            <text x="447" y="85" fontSize="14" fontWeight="600" fill="#4F46E5">Husband</text>
          </g>
        </g>

        {/* Profile 2 - Daughter (right) */}
        <g id="profile-2">
          <g className="profile-image">
            <image
              x="453"
              y="207"
              width="76"
              height="76"
              href="/images/professionals/care-circle-daughter.png"
              clipPath="url(#clipProfile2)"
              preserveAspectRatio="xMidYMid slice"
            />
            <circle cx="491" cy="245" r="38" fill="none" stroke="#99ABAB" strokeWidth="2.4" />
          </g>
          <g className="profile-text">
            <text x="541" y="237" fontSize="17" fontWeight="600" fill="#666666">Beth</text>
            <text x="541" y="257" fontSize="14" fontWeight="600" fill="#4F46E5">Daughter</text>
          </g>
        </g>

        {/* Profile 3 - SLT (bottom right) */}
        <g id="profile-3">
          <g className="profile-image">
            <image
              x="359"
              y="383"
              width="76"
              height="76"
              href="/images/professionals/care-circle-slt.png"
              clipPath="url(#clipProfile3)"
              preserveAspectRatio="xMidYMid slice"
            />
            <circle cx="397" cy="421" r="38" fill="none" stroke="#99ABAB" strokeWidth="2.4" />
          </g>
          <g className="profile-text">
            <text x="447" y="413" fontSize="17" fontWeight="600" fill="#666666">Mandy</text>
            <text x="447" y="433" fontSize="14" fontWeight="600" fill="#4F46E5">SLT</text>
          </g>
        </g>

        {/* Profile 4 - OT (bottom left) */}
        <g id="profile-4">
          <g className="profile-image">
            <image
              x="162"
              y="383"
              width="76"
              height="76"
              href="/images/professionals/care-circle-ot.png"
              clipPath="url(#clipProfile4)"
              preserveAspectRatio="xMidYMid slice"
            />
            <circle cx="200" cy="421" r="38" fill="none" stroke="#99ABAB" strokeWidth="2.4" />
          </g>
          <g className="profile-text">
            <text x="150" y="413" textAnchor="end" fontSize="17" fontWeight="600" fill="#666666">Lucy</text>
            <text x="150" y="433" textAnchor="end" fontSize="14" fontWeight="600" fill="#4F46E5">OT</text>
          </g>
        </g>

        {/* Profile 5 - Carer (left) */}
        <g id="profile-5">
          <g className="profile-image">
            <image
              x="74"
              y="207"
              width="76"
              height="76"
              href="/images/professionals/care-circle-carer.png"
              clipPath="url(#clipProfile5)"
              preserveAspectRatio="xMidYMid slice"
            />
            <circle cx="112" cy="245" r="38" fill="none" stroke="#99ABAB" strokeWidth="2.4" />
          </g>
          <g className="profile-text">
            <text x="62" y="237" textAnchor="end" fontSize="17" fontWeight="600" fill="#666666">Maya</text>
            <text x="62" y="257" textAnchor="end" fontSize="14" fontWeight="600" fill="#4F46E5">Carer</text>
          </g>
        </g>

        {/* Profile 6 - Psychologist (top left) */}
        <g id="profile-6">
          <g className="profile-image">
            <image
              x="162"
              y="36"
              width="76"
              height="76"
              href="/images/professionals/care-circle-psych.png"
              clipPath="url(#clipProfile6)"
              preserveAspectRatio="xMidYMid slice"
            />
            <circle cx="200" cy="74" r="38" fill="none" stroke="#99ABAB" strokeWidth="2.4" />
          </g>
          <g className="profile-text">
            <text x="150" y="66" textAnchor="end" fontSize="17" fontWeight="600" fill="#666666">Abidemi</text>
            <text x="150" y="86" textAnchor="end" fontSize="14" fontWeight="600" fill="#4F46E5">Psychologist</text>
          </g>
        </g>
      </Box>
    </Box>
  );
}
